import React, { useState, useEffect, useRef } from 'react';
import '../styles/SongNamingModal.css';

interface SongNamingModalProps {
  onSave: (songName: string) => void;
  onSkip: () => void;
  defaultName?: string;
  audioData?: string;
}

const MUSIC_EMOJIS = [
  '🎵', '🎶', '🎸', '🎹', '🎤', '🎧', 
  '🥁', '🎺', '🎻', '🎼', '✨', '🌟', 
  '💫', '⭐', '🔥', '💖', '🎨', '🌈'
];

const SongNamingModal: React.FC<SongNamingModalProps> = ({
  onSave,
  onSkip,
  defaultName = '',
  audioData
}) => {
  const [songName, setSongName] = useState(defaultName);
  const [isPlaying, setIsPlaying] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const MAX_LENGTH = 50;

  useEffect(() => {
    // Focus input when modal opens
    inputRef.current?.focus();

    // Create audio element if audioData is provided
    if (audioData) {
      try {
        console.log('🎵 Creating audio element with data:', audioData?.substring(0, 50) + '...');
        
        // Convert base64 to proper audio URL if needed
        let audioUrl = audioData;
        
        // If it's base64 data without data URL prefix, add it
        if (!audioData.startsWith('http') && !audioData.startsWith('data:')) {
          // Assume it's base64 WAV data
          audioUrl = `data:audio/wav;base64,${audioData}`;
        }
        
        const audio = new Audio(audioUrl);
        audio.loop = true; // Loop the audio until modal closes
        audioRef.current = audio;

        // Add event listeners
        audio.addEventListener('ended', () => {
          console.log('🎵 Audio ended');
          setIsPlaying(false);
        });

        audio.addEventListener('error', (e) => {
          console.error('🎵 Audio error:', e);
          setIsPlaying(false);
        });

        audio.addEventListener('loadeddata', () => {
          console.log('🎵 Audio loaded successfully');
        });

        // Auto-play when modal opens
        audio.play().then(() => {
          console.log('🎵 Audio playing');
          setIsPlaying(true);
        }).catch(err => {
          console.warn('🎵 Auto-play prevented or failed:', err);
          setIsPlaying(false);
        });
      } catch (error) {
        console.error('🎵 Error loading audio:', error);
      }
    } else {
      console.log('🎵 No audio data provided');
    }

    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, [audioData]);

  const togglePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(err => {
        console.error('Error playing audio:', err);
      });
    }
  };

  const handleEmojiClick = (emoji: string) => {
    if (songName.length < MAX_LENGTH) {
      setSongName(prev => prev + emoji);
      inputRef.current?.focus();
    }
  };

  const handleSave = () => {
    const trimmedName = songName.trim();
    if (trimmedName) {
      onSave(trimmedName);
    } else {
      onSkip();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onSkip();
    }
  };

  return (
    <div className="song-naming-modal-overlay" onClick={onSkip}>
      <div className="song-naming-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onSkip}>×</button>

        <div className="modal-header">
          <div className="modal-icon">🎵</div>
          <h2 className="modal-title">Name Your Song!</h2>
          
          {audioData && (
            <button 
              className="play-pause-button"
              onClick={togglePlayPause}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              <span className="play-pause-icon">
                {isPlaying ? '⏸️' : '▶️'}
              </span>
              <span className="play-pause-text">
                {isPlaying ? 'Pause' : 'Play'} Song
              </span>
            </button>
          )}
        </div>

        <div className="modal-body">
          <div className="input-section">
            <input
              ref={inputRef}
              type="text"
              className="song-name-input"
              placeholder="My Awesome Song..."
              value={songName}
              onChange={(e) => setSongName(e.target.value.slice(0, MAX_LENGTH))}
              onKeyDown={handleKeyDown}
              maxLength={MAX_LENGTH}
            />
            <div className="character-count">
              {songName.length}/{MAX_LENGTH}
            </div>
          </div>

          <div className="emoji-section">
            <div className="emoji-label">Add some flair:</div>
            <div className="emoji-grid">
              {MUSIC_EMOJIS.map((emoji, index) => (
                <button
                  key={index}
                  className="emoji-button"
                  onClick={() => handleEmojiClick(emoji)}
                  disabled={songName.length >= MAX_LENGTH}
                  title={`Add ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="skip-button" onClick={onSkip}>
            Skip
          </button>
          <button className="save-button" onClick={handleSave}>
            <span className="save-icon">💾</span>
            <span>Save Song</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SongNamingModal;
